import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal, RevealGroup } from '@/components/atoms';
import { StoryFactCard } from '@/components/molecules';
import { type CompanyStoryBandModel } from '@/hooks/useCompanyStoryBand';

import { CompanyStoryIntro } from './CompanyStoryIntro';

/**
 * The company story in one band, for a page lending it a slot.
 *
 * This is the layout that answers the brief behind the whole component: /about
 * lives inside a "Company" dropdown that visitors were not opening, so the
 * page's opening claim now also appears part-way down the five pages they do
 * reach, with a link to the rest of it.
 *
 * ## Why it is not the full band at a smaller size
 *
 * The full band is an argument that needs a column of narrative and a sticky
 * question beside it; it is the first thing on a page whose whole job is
 * identity. Here it is one band among a dozen on a page about something else,
 * so it states the claim once and gets out of the way: two sentences, three
 * facts, one link. The sentences are `summary` rather than the first of
 * `paragraphs` — written for this length rather than cut to it, and leaving
 * the years to the cards so nothing is said twice.
 *
 * ## The proportions
 *
 * Six and six. The narrative column is the one with a reading measure to
 * honour and the cards are the one with a fixed amount to say, so an uneven
 * split would either crowd the sentences or stretch the cards; at six each the
 * copy lands near 58ch, which the `maxWidth` below holds it to on a wide
 * screen.
 *
 * ## Across widths
 *
 * One column below `md`, copy then cards, which is the order the claim wants
 * to be read in. The cards themselves never reflow: `StoryFactCard` is laid
 * out across rather than down, so three stacked rows work at 360px and at
 * 600px alike, and there is no breakpoint at which three cards leave an
 * orphan on a row. That is why there is no two-column step at `sm` — a 2+1
 * grid would be the only thing on the band that looked like a mistake.
 *
 * One `RevealGroup` around the cards rather than a `Reveal` each, so the
 * stagger costs one IntersectionObserver instead of three.
 */
export function CompanyStoryCompact({ model }: { model: CompanyStoryBandModel }) {
  const { titleId, eyebrow, title, lede, cta, narrative, facts } = model;

  return (
    <Grid container spacing={{ xs: 8, md: 12 }} alignItems="center">
      <Grid size={{ xs: 12, md: 6 }}>
        <Reveal direction="left">
          <CompanyStoryIntro
            titleId={titleId}
            eyebrow={eyebrow}
            title={title}
            lede={lede}
            cta={cta}
          >
            <Stack spacing={4}>
              {narrative.map((paragraph) => (
                <Typography key={paragraph} color="text.secondary" sx={{ maxWidth: '58ch' }}>
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </CompanyStoryIntro>
        </Reveal>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <RevealGroup
          component="dl"
          direction="right"
          step={70}
          sx={{ m: 0, display: 'grid', gap: { xs: 5, md: 6 } }}
        >
          {facts.map((fact) => (
            <StoryFactCard key={fact.label} fact={fact} />
          ))}
        </RevealGroup>
      </Grid>
    </Grid>
  );
}
